import type { RegionId } from "@/lib/regionalSites";

const CURRENCY_PLACEHOLDER = "\uE000";

function applyCasing(source: string, replacement: string): string {
  if (source === source.toUpperCase()) return replacement.toUpperCase();
  if (source[0] === source[0]?.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/** Consumer-facing tax name for the active regional site. */
export function regionalTaxTerm(regionId: RegionId): string {
  switch (regionId) {
    case "uk":
      return "VAT";
    case "us":
      return "Sales Tax";
    default:
      return "GST";
  }
}

/** Title-case tax label for section headings. */
export function regionalTaxTermTitle(regionId: RegionId): string {
  const term = regionalTaxTerm(regionId);
  return term === "sales tax" ? "Sales Tax" : term;
}

export function regionalMoneyUnit(regionId: RegionId): string {
  return regionId === "uk" ? "pound" : "dollar";
}

export function regionalMoneyUnits(regionId: RegionId): string {
  return regionId === "uk" ? "pounds" : "dollars";
}

export function regionalCurrencySymbol(regionId: RegionId): string {
  return regionId === "uk" ? "£" : "$";
}

const amountLocale: Record<RegionId, string> = {
  au: "en-AU",
  uk: "en-GB",
  us: "en-US",
};

/** Whole-number marketing amount in local currency, e.g. £200 or $500,000. */
export function formatRegionalAmount(amount: number, regionId: RegionId): string {
  const symbol = regionalCurrencySymbol(regionId);
  const formatted = amount.toLocaleString(amountLocale[regionId], {
    maximumFractionDigits: 0,
  });
  return `${symbol}${formatted}`;
}

export function regionalTaxDeductionsHighlight(regionId: RegionId): string {
  return `Matched to transactions, ${regionalTaxTerm(regionId)}, and deductions`;
}

export function regionalTaxSectionTitle(regionId: RegionId): string {
  return `${regionalTaxTermTitle(regionId)}, Broken Out for You`;
}

export function regionalTaxSectionIntro(regionId: RegionId): string {
  const term = regionalTaxTerm(regionId);
  return `If you run a business, your purchases include ${term}, and you can usually claim back the ${term} paid on eligible business purchases. So it matters how much of each purchase was tax, not just the total. When the app scans a receipt it captures the total, the tax component, and the net amount, giving you a clear record of what you can potentially get back.`;
}

export function regionalSupportedBanks(regionId: RegionId): string {
  switch (regionId) {
    case "uk":
      return "Lloyds, Barclays, HSBC, NatWest, and Monzo";
    case "us":
      return "Chase, Bank of America, Wells Fargo, Citi, and Capital One";
    default:
      return "Commonwealth Bank, Westpac, ANZ, NAB, and ING";
  }
}

/**
 * On the UK site, rewrite illustrative $ amounts and "dollar" wording to pounds.
 * AU and US keep canonical copy. Skips A$/US$/£ amounts and named foreign currencies.
 */
export function regionalizeMarketingText(text: string, regionId: RegionId): string {
  if (!text || regionId !== "uk") return text;

  const preserved: string[] = [];
  const preserve = (match: string) => {
    preserved.push(match);
    return `${CURRENCY_PLACEHOLDER}${preserved.length - 1}${CURRENCY_PLACEHOLDER}`;
  };

  let next = text
    .replace(/\b(?:A|US)\$[0-9][0-9,]*(?:\.[0-9]+)?/g, preserve)
    .replace(/£[0-9][0-9,]*(?:\.[0-9]+)?/g, preserve)
    .replace(/&pound;[0-9][0-9,]*(?:\.[0-9]+)?/g, preserve)
    .replace(/\b(?:US|Australian) dollars?\b/gi, preserve);

  next = next.replace(/(?<![A-Za-z£])\$([0-9][0-9,]*(?:\.[0-9]+)?)/g, "£$1");
  next = next.replace(/\bdollars\b/gi, (match) => applyCasing(match, "pounds"));
  next = next.replace(/\bdollar\b/gi, (match) => applyCasing(match, "pound"));

  next = next.replace(
    new RegExp(`${CURRENCY_PLACEHOLDER}(\\d+)${CURRENCY_PLACEHOLDER}`, "g"),
    (_full, index: string) => preserved[Number(index)] ?? _full,
  );

  return next;
}

/** Apply {@link regionalizeMarketingText} to HTML text nodes only. */
export function regionalizeMarketingHtml(html: string, regionId: RegionId): string {
  if (!html || regionId !== "uk") return html;
  return html.replace(/(^|>)([^<]*)/g, (_full, prefix: string, text: string) => {
    return `${prefix}${regionalizeMarketingText(text, regionId)}`;
  });
}
